<div class="page">
  <!-- ===================== PAGE HEAD ===================== -->
  <div class="page-head">
    <div>
      <h2>{{ pageTitle() || 'Partner Dashboard' }}</h2>
      <p>Your members, premiums and certificates — one place.</p>
      <div class="head-meta">
        <span class="meta-pill"><i class="pi pi-building"></i> {{ partnerLabel() }}</span>
        @if (period(); as pr) {
          <span class="meta-pill"><i class="pi pi-calendar"></i>
            {{ pr.fromDate ? (pr.fromDate | indDate) : 'All time' }}@if (pr.fromDate && pr.toDate) { – {{ pr.toDate | indDate }} }
          </span>
        }
        @if (scope()?.isAdminView) { <span class="meta-pill admin"><i class="pi pi-verified"></i> Admin view</span> }
        @if (testMode.showingTest()) {
          <!-- Everything below is practice data. Without this the numbers read as real business. -->
          <span class="meta-pill test"><i class="pi pi-circle-fill"></i> Test mode</span>
        }
      </div>
    </div>

    <div class="head-actions">
      @if (lastLoaded(); as t) {
        <span class="freshness"><i class="pi pi-clock"></i> Updated {{ t | date: 'h:mm a' }}</span>
      }
      <p-button label="Refresh" icon="pi pi-refresh" [outlined]="true" severity="secondary" size="small"
        [loading]="overviewLoading()" (onClick)="loadOverview()" />
    </div>
  </div>

  <!-- Primary tasks, kept out of the metrics so they stay findable. -->
  <div class="quick-bar">
    <p-button label="Member Onboarding" icon="pi pi-user-plus" size="small" routerLink="/member-onboarding" />
    <p-button label="Member Lookup" icon="pi pi-id-card" size="small" [outlined]="true" routerLink="/member-lookup" />
    <p-button label="Detailed Records" icon="pi pi-table" size="small" [outlined]="true" routerLink="/partner-records" />
  </div>

  <!-- ===================== FILTER BAR ===================== -->
  <app-partner-filter-bar [isAdmin]="isAdmin" [partnerOptions]="partnerOptions()" (filterChange)="onFilter($event)" />

  <!-- Members who paid but whose certificate was never produced. Hidden when there are none. -->
  <app-unissued-panel (issued)="loadOverview()" />

  <!-- ===================== HEADLINE KPIs ===================== -->
  <!-- Four hero tiles, then eight compact ones — both grids use a fixed column count that
       divides their item count exactly, so a row never ends in a ragged gap. -->
  @if (overviewLoading() && !summary()) {
    <div class="kpis hero">
      @for (i of [1,2,3,4]; track i) {
        <div class="kpi"><p-skeleton height="1rem" width="55%" /><p-skeleton height="2rem" width="70%" /></div>
      }
    </div>
  } @else if (summary(); as s) {
    <div class="kpis hero">
      <div class="kpi k-money">
        <span class="kpi-label"><i class="pi pi-indian-rupee"></i> Collected</span>
        <strong class="kpi-value good">{{ inr(s.collectedAmount) }}</strong>
        <div class="meter" role="progressbar" [attr.aria-valuenow]="collectionRate()" aria-valuemin="0" aria-valuemax="100">
          <span class="meter-fill good" [style.width.%]="collectionRate()"></span>
        </div>
        <small class="kpi-sub">{{ collectionRate() }}% of {{ inrShort(s.totalPremium) }} billed</small>
      </div>

      <div class="kpi k-warn">
        <span class="kpi-label"><i class="pi pi-hourglass"></i> Pending</span>
        <strong class="kpi-value warn">{{ inr(s.pendingAmount) }}</strong>
        <div class="meter"><span class="meter-fill warn" [style.width.%]="100 - collectionRate()"></span></div>
        <small class="kpi-sub">{{ num(s.unpaidMembers) }} members yet to pay</small>
      </div>

      <div class="kpi">
        <span class="kpi-label"><i class="pi pi-users"></i> Total members</span>
        <strong class="kpi-value">{{ num(s.totalMembers) }}</strong>
        <div class="split-mini">
          <span class="dot ok"></span>{{ num(s.paidMembers) }} paid
          <span class="dot due"></span>{{ num(s.unpaidMembers) }} unpaid
        </div>
        <small class="kpi-sub">avg premium {{ inr(avgPremium()) }}</small>
      </div>

      <div class="kpi">
        <span class="kpi-label"><i class="pi pi-verified"></i> KYC verified</span>
        <strong class="kpi-value">{{ num(s.kycVerified) }}</strong>
        <div class="meter"><span class="meter-fill" [style.width.%]="kycRate()"></span></div>
        <small class="kpi-sub">
          {{ kycRate() }}% of members · {{ num(s.kycPending) }} pending
        </small>
      </div>
    </div>

    <!-- ===================== SECONDARY METRICS ===================== -->
    <div class="kpis mini">
      <div class="kpi-s">
        <i class="pi pi-check-circle"></i>
        <div><strong>{{ successRate() }}%</strong><span>Payment success rate</span></div>
      </div>
      <div class="kpi-s">
        <i class="pi pi-wallet"></i>
        <div><strong>{{ inrShort(paymentStats()?.successAmount) }}</strong><span>Taken through gateways</span></div>
      </div>
      <div class="kpi-s">
        <i class="pi pi-credit-card"></i>
        <div><strong>{{ num(paymentStats()?.totalTransactions) }}</strong><span>Total transactions</span></div>
      </div>
      <div class="kpi-s">
        <i class="pi pi-thumbs-up"></i>
        <div><strong>{{ num(paymentStats()?.successCount) }}</strong><span>Successful transactions</span></div>
      </div>
      <div class="kpi-s" [class.alert]="(paymentStats()?.failedCount ?? 0) > 0">
        <i class="pi pi-times-circle"></i>
        <div><strong>{{ num(paymentStats()?.failedCount) }}</strong><span>Failed transactions</span></div>
      </div>
      <div class="kpi-s">
        <i class="pi pi-sync"></i>
        <div><strong>{{ num(paymentStats()?.pendingCount) }}</strong><span>In-flight transactions</span></div>
      </div>
      <div class="kpi-s" [class.alert]="(s.kycPending ?? 0) > 0">
        <i class="pi pi-id-card"></i>
        <div><strong>{{ num(s.kycPending) }}</strong><span>KYC pending</span></div>
      </div>
      <div class="kpi-s">
        <i class="pi pi-replay"></i>
        <div><strong>{{ num(s.refundedMembers) }}</strong><span>Refunded members</span></div>
      </div>
    </div>
  }

  <!-- ===================== NOTHING IN THIS PERIOD ===================== -->
  <!-- A wall of zeros never says whether the account is new or the filter is simply too narrow.
       Looking at one financial year while the work sits in another is by far the commonest
       cause, so the fix is offered here rather than left to be discovered. -->
  @if (isEmpty()) {
    <div class="card empty-card">
      <i class="pi pi-users"></i>
      <h3>No members in this period</h3>
      <p>
        Nothing has been onboarded in
        {{ period()?.fromDate ? 'the selected period' : 'any period' }} yet — or your members were
        enrolled outside it. Widening the period is the quickest way to tell which.
      </p>
      <div class="empty-actions">
        <p-button label="Onboard a member" icon="pi pi-user-plus" routerLink="/member-onboarding" />
        @if (period()?.fromDate) {
          <p-button label="Show all time" icon="pi pi-history" [outlined]="true" (onClick)="showAllTime()" />
        }
      </div>
    </div>
  } @else if (isBrowser) {

  <!-- ===================== CHARTS ===================== -->
  <div class="charts">
    <div class="card chart-card">
      <div class="chart-head"><h3>Monthly trend</h3><small>Collected &amp; pending vs members</small></div>
      @if (hasTrend()) {
        <div class="chart-box tall"><p-chart type="bar" [data]="trendData()" [options]="trendOptions" /></div>
      } @else {
        <!-- A canvas with nothing to plot paints a blank rectangle, which is what made this
             card read as dead space. Say why it is empty instead. -->
        <div class="chart-empty">
          <i class="pi pi-chart-bar"></i>
          <p>No enrolments in this period</p>
          <small>Widen the date range or clear a filter to see the trend.</small>
        </div>
      }
    </div>

    <div class="card chart-card">
      <div class="chart-head"><h3>Payment mix</h3><small>By transaction status</small></div>
      @if (hasMix()) {
        <div class="chart-box tall"><p-chart type="doughnut" [data]="mixData()" [options]="doughnutOptions" /></div>
      } @else {
        <div class="chart-empty">
          <i class="pi pi-credit-card"></i>
          <p>No transactions yet</p>
          <small>Payment attempts appear here as they happen.</small>
        </div>
      }
    </div>
  </div>

  <!-- ===================== TRANSACTION HEALTH ===================== -->
  @if (txMix().total > 0) {
    <div class="card split-card">
      <div class="chart-head"><h3>Transaction health</h3><small>{{ num(txMix().total) }} attempts in this period</small></div>
      <!-- Three categories say it in a 14px bar; a second doughnut would only repeat the one above. -->
      <div class="split-bar">
        <span class="seg-ok" [style.width.%]="txMix().okPct" [pTooltip]="txMix().ok + ' successful'"></span>
        <span class="seg-bad" [style.width.%]="txMix().badPct" [pTooltip]="txMix().bad + ' failed'"></span>
        <span class="seg-wait" [style.width.%]="txMix().waitPct" [pTooltip]="txMix().wait + ' in flight'"></span>
      </div>
      <ul class="split-legend">
        <li><span class="dot ok"></span> Successful <strong>{{ num(txMix().ok) }}</strong><em>{{ txMix().okPct }}%</em></li>
        <li><span class="dot bad"></span> Failed <strong>{{ num(txMix().bad) }}</strong><em>{{ txMix().badPct }}%</em></li>
        <li><span class="dot wait"></span> In flight <strong>{{ num(txMix().wait) }}</strong><em>{{ txMix().waitPct }}%</em></li>
      </ul>
    </div>
  }

  <!-- ===================== RECENT MEMBERS ===================== -->
  <div class="card recent">
    <div class="chart-head">
      <h3>Recent members</h3>
      <a routerLink="/partner-records" class="see-all">See all <i class="pi pi-arrow-right"></i></a>
    </div>
    <p-table [value]="recentMembers()" [loading]="overviewLoading()" dataKey="id" styleClass="p-datatable-sm">
      <ng-template pTemplate="header">
        <tr>
          <th>Series No</th>
          <th>Name</th>
          <th>Mobile</th>
          <th>Policy</th>
          <th class="ta-r">Gross</th>
          <th>Payment</th>
          <th>KYC</th>
          <th>Created</th>
          <th class="ta-r">Action</th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-r>
        <tr>
          <td class="mono">{{ r.memberSeriesNo || '—' }}</td>
          <td class="name">{{ r.fullName || '—' }}</td>
          <td class="mono">{{ r.mobileNumber || '—' }}</td>
          <td>
            {{ r.masterPolicyName || '—' }}
            <!-- The policy NUMBER is what the customer asks about; showing only the product name
                 hid the fact that some paid members never got one. -->
            @if (r.policyNo) {
              <small class="sub mono">{{ r.policyNo }}</small>
            } @else if (isStranded(r)) {
              <small class="sub warn">not issued</small>
            }
          </td>
          <td class="ta-r amount">{{ inr(r.grossAmt) }}</td>
          <td><p-tag [value]="r.paymentStatus || '—'" [severity]="paySeverity(r.paymentStatus)" /></td>
          <td><p-tag [value]="r.kycStatus || '—'" [severity]="kycSeverity(r.kycStatus)" /></td>
          <td class="nowrap">{{ r.createdAt ? (r.createdAt | indDate) : '—' }}</td>
          <td class="ta-r">
            @if (needsPayment(r.paymentStatus)) {
              <p-button label="Collect" icon="pi pi-credit-card" size="small" [rounded]="true"
                (onClick)="collect({ id: r.id, name: r.fullName, mobile: r.mobileNumber, email: r.email, amount: r.grossAmt, kycStatus: r.kycStatus })" />
            } @else if (hasPolicy(r)) {
              <p-button label="Certificate" icon="pi pi-id-card" size="small" [rounded]="true"
                [outlined]="true" [routerLink]="['/policies/certificate', r.id]" />
            } @else { <span class="muted">—</span> }
          </td>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage">
        <tr><td colspan="9"><div class="state"><i class="pi pi-inbox"></i><p>No recent members.</p></div></td></tr>
      </ng-template>
    </p-table>
  </div>

  }

  <!-- inline collect-payment flow: opens the gateway this partner is assigned, in place -->
  <app-collect-payment-flow (paid)="onPaid()" />
</div>
